<template>
    <div>
        <custom-table
            :title="$t('pages.ecommerce.product.module.title')"
            rowKey="id"
            :items="table.data || []"
            :loading="table.loading"
            selectableRows
            :emptyTableText="$t('pages.ecommerce.product.module.no_set_module')"
            :columns="fields"
            :actions="tableActions"
            @action="handleClickAction"
            @selectableRows="handleSelectedRow">
            <template v-slot:module_name="{ row: record }">
                {{ record.module?.name }}
            </template>
            <template v-slot:daily_limit="{ row: record }">
                {{ formatLimit(record.daily_limit) }}
            </template>
            <template v-slot:weekly_limit="{ row: record }">
                {{ formatLimit(record.weekly_limit) }}
            </template>
            <template v-slot:monthly_limit="{ row: record }">
                {{ formatLimit(record.monthly_limit) }}
            </template>
            <template v-slot:yearly_limit="{ row: record }">
                {{ formatLimit(record.yearly_limit) }}
            </template>
            <template v-slot:actions="{ row: record }">
                <div class="d-flex justify-content-end">
                    <el-tooltip :content="$t('common.edit')" placement="top">
                        <a @click="editModule(record)" class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1">
                            <span class="svg-icon svg-icon-3">
                                <inline-svg src="/media/icons/duotune/art/art005.svg"/>
                            </span>
                        </a>
                    </el-tooltip>
                    <a @click="deleteModule(record)" class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm">
                        <span class="svg-icon svg-icon-3">
                            <inline-svg src="/media/icons/duotune/general/gen027.svg"/>
                        </span>
                    </a>
                </div>
            </template>
        </custom-table>

        <div class="modal fade" id="kt_modal_product_module" ref="moduleModal" tabindex="-1" aria-hidden="true">
            <div class="modal-dialog modal-dialog-centered mw-650px">
                <div class="modal-content">
                    <div class="modal-header">
                        <h2 class="fw-bolder">{{ form.title }}</h2>
                        <div data-bs-dismiss="modal" class="btn btn-icon btn-sm btn-active-icon-primary">
                            <span class="svg-icon svg-icon-1">
                                <inline-svg src="/media/icons/duotune/arrows/arr061.svg"/>
                            </span>
                        </div>
                    </div>
                    <el-form @submit.prevent="onSubmit()" :model="form.data" ref="moduleForm">
                        <div class="modal-body py-10 px-lg-17">
                            <div class="fv-row mb-1 mt-3">
                                <label class="required fs-6 fw-bold mb-2">{{ $t('pages.ecommerce.product.module.cols.module_name') }}</label>
                                <el-form-item prop="module_id" :rules="$validation.getMessage(['required'])">
                                    <el-select v-model="form.data.module_id" :disabled="form.updateStatus" filterable class="w-100" :placeholder="$t('common.chooseSelect')">
                                        <el-option-group
                                            v-for="kind in availableModules"
                                            :key="kind.id"
                                            :label="kind.name">
                                            <el-option
                                                v-for="m in kind.modules"
                                                :key="m.id"
                                                :label="m.name"
                                                :value="m.id"/>
                                        </el-option-group>
                                    </el-select>
                                </el-form-item>
                            </div>

                            <div class="row">
                                <div class="col-md-6 fv-row mb-1 mt-3">
                                    <label class="fs-6 fw-bold mb-2">{{ $t('pages.ecommerce.product.module.cols.daily_limit') }}</label>
                                    <el-form-item prop="daily_limit">
                                        <el-input v-model="form.data.daily_limit" oninput="value=value.replace(/[^0-9]/g,'')" :placeholder="$t('pages.ecommerce.product.module.unlimited')"/>
                                    </el-form-item>
                                </div>

                                <div class="col-md-6 fv-row mb-1 mt-3">
                                    <label class="fs-6 fw-bold mb-2">{{ $t('pages.ecommerce.product.module.cols.weekly_limit') }}</label>
                                    <el-form-item prop="weekly_limit">
                                        <el-input v-model="form.data.weekly_limit" oninput="value=value.replace(/[^0-9]/g,'')" :placeholder="$t('pages.ecommerce.product.module.unlimited')"/>
                                    </el-form-item>
                                </div>

                                <div class="col-md-6 fv-row mb-1 mt-3">
                                    <label class="fs-6 fw-bold mb-2">{{ $t('pages.ecommerce.product.module.cols.monthly_limit') }}</label>
                                    <el-form-item prop="monthly_limit">
                                        <el-input v-model="form.data.monthly_limit" oninput="value=value.replace(/[^0-9]/g,'')" :placeholder="$t('pages.ecommerce.product.module.unlimited')"/>
                                    </el-form-item>
                                </div>

                                <div class="col-md-6 fv-row mb-1 mt-3">
                                    <label class="fs-6 fw-bold mb-2">{{ $t('pages.ecommerce.product.module.cols.yearly_limit') }}</label>
                                    <el-form-item prop="yearly_limit">
                                        <el-input v-model="form.data.yearly_limit" oninput="value=value.replace(/[^0-9]/g,'')" :placeholder="$t('pages.ecommerce.product.module.unlimited')"/>
                                    </el-form-item>
                                </div>
                            </div>

                            <small class="text-muted d-block mt-2">{{ $t('pages.ecommerce.product.module.limit_help') }}</small>
                        </div>

                        <div class="modal-footer flex-center">
                            <button :data-kt-indicator="form.loading ? 'on' : null" class="btn btn-lg btn-primary" type="submit" :disabled="form.loading">
                                <span v-if="!form.loading" class="indicator-label">{{ $t("btn.save") }}</span>
                                <span v-if="form.loading" class="indicator-progress">
                                    {{ $t("messages.wait") }}
                                    <span class="spinner-border spinner-border-sm align-middle ms-2"></span>
                                </span>
                            </button>
                        </div>
                    </el-form>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import CustomTable from "@/components/custom-table";

export default {
    name: "ProductModuleList",
    props: ['productID'],
    components: {CustomTable},
    data() {
        return {
            fields: [
                {name: this.$t("pages.ecommerce.product.module.cols.module_name"), scopedSlots: {customRender: "module_name"}},
                {name: this.$t("pages.ecommerce.product.module.cols.daily_limit"), scopedSlots: {customRender: "daily_limit"}},
                {name: this.$t("pages.ecommerce.product.module.cols.weekly_limit"), scopedSlots: {customRender: "weekly_limit"}},
                {name: this.$t("pages.ecommerce.product.module.cols.monthly_limit"), scopedSlots: {customRender: "monthly_limit"}},
                {name: this.$t("pages.ecommerce.product.module.cols.yearly_limit"), scopedSlots: {customRender: "yearly_limit"}},
                {name: this.$t("common.action"), key: "action", scopedSlots: {customRender: "actions"}},
            ],
            form: {
                title: '',
                loading: false,
                updateStatus: false,
                data: {},
            },
            tableActions: [
                {name: "new", icon: "bi-plus-lg", label: "btn.add"},
                {name: "refresh", icon: "bi-arrow-repeat", label: "btn.refresh"},
                {name: "delete", icon: "bi-trash", label: "btn.delete"}
            ],
            selectedRowKeys: [],
        }
    },
    computed: {
        table() {
            return this.$store.state.ecommerce.product.module.table;
        },
        allModules() {
            return this.$store.state.module.table?.data || [];
        },
        availableModules() {
            const addedIds = (this.table.data || []).map(m => m.module_id);
            if (this.form.updateStatus) {
                return this.allModules
                    .map(kind => ({
                        ...kind,
                        modules: kind.modules.filter(m => m.id === this.form.data.module_id || !addedIds.includes(m.id))
                    }))
                    .filter(kind => kind.modules.length > 0);
            }
            return this.allModules
                .map(kind => ({
                    ...kind,
                    modules: kind.modules.filter(m => !addedIds.includes(m.id))
                }))
                .filter(kind => kind.modules.length > 0);
        },
    },
    mounted() {
        this.refreshList();
        this.$store.dispatch('module/get', {});
    },
    methods: {
        refreshList() {
            this.$store.dispatch('ecommerce/product/module/get', {product_id: this.productID});
        },
        formatLimit(val) {
            if (val === null || val === undefined || val === '') {
                return this.$t('pages.ecommerce.product.module.unlimited');
            }
            return val;
        },
        addModule() {
            this.resetForm();
            this.form.title = this.$t('pages.ecommerce.product.module.newTitle');
            this.showModal(this.$refs.moduleModal);
        },
        handleClickAction(name) {
            switch (name) {
                case "new":
                    this.addModule();
                    break;
                case "refresh":
                    this.refreshList();
                    break;
                case "delete":
                    this.bulkDelete();
                    break;
            }
        },
        handleSelectedRow(record) {
            this.selectedRowKeys = record;
        },
        async bulkDelete() {
            const ids = this.selectedRowKeys.flat();
            if (!ids.length) {
                this.$message.warning(this.$t('messages.selectRow'));
                return;
            }
            for (const id of ids) {
                await this.$store.dispatch('ecommerce/product/module/delete', {
                    id,
                    product_id: this.productID,
                });
            }
            this.selectedRowKeys = [];
            this.refreshList();
        },
        editModule(record) {
            this.resetForm();
            this.form.updateStatus = true;
            this.form.title = this.$t('pages.ecommerce.product.module.editTitle');
            this.form.data = {
                id: record.id,
                module_id: record.module_id,
                daily_limit: record.daily_limit,
                weekly_limit: record.weekly_limit,
                monthly_limit: record.monthly_limit,
                yearly_limit: record.yearly_limit,
            };
            this.showModal(this.$refs.moduleModal);
        },
        deleteModule(record) {
            this.eventBus.$emit('showConfirmDialog', {
                title: this.$t('btn.delete'),
                message: this.$t('messages.sureDelete'),
                confirmType: 'danger',
                onConfirm: async () => {
                    const response = await this.$store.dispatch('ecommerce/product/module/delete', {
                        id: record.id,
                        product_id: this.productID,
                    });
                    if (response?.status) {
                        this.onResponse(response, () => this.refreshList());
                    }
                },
            });
        },
        onSubmit() {
            this.$refs.moduleForm.validate(async (valid) => {
                if (!valid) return false;

                this.form.loading = true;

                const payload = {...this.form.data};
                ['daily_limit', 'weekly_limit', 'monthly_limit', 'yearly_limit'].forEach(k => {
                    if (payload[k] === '' || payload[k] === undefined) payload[k] = null;
                });

                try {
                    const response = this.form.updateStatus
                        ? await this.$store.dispatch('ecommerce/product/module/update', {
                            id: this.form.data.id,
                            product_id: this.productID,
                            formData: payload,
                        })
                        : await this.$store.dispatch('ecommerce/product/module/add', {
                            product_id: this.productID,
                            formData: payload,
                        });

                    if (response?.status) {
                        this.onResponse(response, () => {
                            this.hideModal(this.$refs.moduleModal);
                            this.refreshList();
                        });
                    }
                } finally {
                    this.form.loading = false;
                }
            });
        },
        resetForm() {
            this.form = {
                title: '',
                loading: false,
                updateStatus: false,
                data: {
                    module_id: undefined,
                    daily_limit: null,
                    weekly_limit: null,
                    monthly_limit: null,
                    yearly_limit: null,
                },
            };
        },
    }
}
</script>
